/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import XLoader from "../loader/XLoader";
import useAxios from "../../hooks/useAxios";
import { API_ENDPOINTS } from "../../constants/api";
import { mergeTags, rssMergeTags } from "./utils/stripo.constant";
import { CampaignType } from "../../utils/enums";
import { Button, Alert, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import useSidebar from "../../hooks/useSidebar";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPredefinedFieldRequest } from "../../redux/modules/settings/settingsSlice";
import { RootState } from "../../redux/rootReducer";

interface StripoProps {
  html?: string;
  css?: string;
  rssFeedId?: number | string;
  onRssFeedChange?: (id: string) => void;
  rssMaxArticles?: number;
  onRssMaxArticlesChange?: (count: number) => void;
}

export default function Stripo(props: StripoProps) {
  const { html, css, rssFeedId, onRssFeedChange, rssMaxArticles = 3, onRssMaxArticlesChange } = props;
  const { fetchData, postData } = useAxios()
  const { onShowSidebar } = useSidebar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { predefinedFieldList, loading: predefinedFieldsLoading } = useSelector((state: RootState) => state.settings);
  const { orgLocationMap, selectedLocationId } = useSelector((state: RootState) => state.user);
  const rssFeeds = orgLocationMap?.[+selectedLocationId]?.rss_feeds || [];
  const [fieldsRequested, setFieldsRequested] = useState(false);
  const [rssCustomTags, setRssCustomTags] = useState<any[]>([]);
  const [rssTagsReady, setRssTagsReady] = useState(false);

  const customMergeTags = predefinedFieldList?.customFields?.map((field: any) => ({
    label: field.name,
    value: `{{${field.name}}}`,
    type: "Email-SMS"
  })) || [];

  useEffect(() => {
    dispatch(getPredefinedFieldRequest());
    setFieldsRequested(true);
    setRssTagsReady(false);
    if (!rssFeedId) {
      setRssCustomTags([]);
      setRssTagsReady(true);
      return;
    }
    (async () => {
      const response = await postData(API_ENDPOINTS.LOCATION_RSS_PREVIEW, { rssFeedId });
      const tags = response?.success
        ? (response.data?.customTags || []).map((tag: any) => ({
            label: tag.label,
            value: tag.value,
            hint: tag.hint,
            type: CampaignType.EMAIL,
          }))
        : [];
      setRssCustomTags(tags);
      setRssTagsReady(true);
    })();
  }, [rssFeedId]);

  const activeMergeTags = [
    {
      category: "Patient Campaign",
      entries: mergeTags,
    },
    {
      category: "Custom Fields",
      entries: customMergeTags,
    },
    ...(rssFeedId
      ? [
          {
            category: "RSS Feed",
            entries: rssMergeTags,
          },
          ...(rssCustomTags.length
            ? [{ category: "RSS Custom Fields", entries: rssCustomTags }]
            : []),
        ]
      : []),
  ];

  useEffect(() => {
    if (typeof window?.StripoApi?.setMergeTags === 'function') {
      window.StripoApi.setMergeTags(activeMergeTags);
    }
  }, [rssFeedId, rssCustomTags]);

  useEffect(() => {
    // Wait for the custom-fields request to actually finish (success OR
    // failure) before initializing Stripo, otherwise this effect re-runs
    // when the result arrives and re-initializes Stripo a second time,
    // discarding any edits made in the first instance's iframe. 
    const predefinedFieldsReady = fieldsRequested && !predefinedFieldsLoading && rssTagsReady;
    if ((html || css) && predefinedFieldsReady) {
      const initStripo = () => {
        const apiRequestData = {
          emailId: 123,
        };
        const script = document.createElement("script");
        script.id = "stripoScript";
        script.type = "text/javascript";
        script.src = "https://plugins.stripo.email/static/latest/stripo.js";
        script.onload = function () {
          window.Stripo.init({
            unSubscribeLink: "{{unsubscribeURL}}",
            // socialNetworks: [
            //   {
            //     name: "twitter",
            //     href: "https://twitter.com/my-twitter",
            //   },
            //   {
            //     name: "facebook",
            //     href: "https://facebook.com/my-facebook",
            //   },
            // ],
            mergeTags: activeMergeTags,
            customAppearanceMergetags: true,
            mergeTagsOptions: {
              useIcon: true,
              title: "merge tag"
            },
            // specialLinks: [
            //   {
            //     category: "eSputnik",
            //     entries: [
            //       { label: "Unsubscribe", value: "https://esputnik.com/unsubscribe" },
            //       {
            //         label: "View in browser",
            //         value: "https://esputnik.com/viewInBrowser",
            //       },
            //     ],
            //   },
            //   {
            //     category: "Other",
            //     entries: [
            //       {
            //         label: "Some special link",
            //         value: "https://some.special.link.url",
            //       },
            //     ],
            //   },
            // ],
            settingsId: "stripoSettingsContainer",
            previewId: "stripoPreviewContainer",
            codeEditorButtonId: "codeEditor",
            undoButtonId: "undoButton",
            redoButtonId: "redoButton",
            locale: "en",
            html: html,
            css: css,
            apiRequestData: apiRequestData,
            userFullName: "Plugin Demo User",
            // versionHistory: {
            //   changeHistoryLinkId: "changeHistoryLink",
            //   onInitialized: function (lastChangeIndoText) {
            //     $("#changeHistoryContainer").show();
            //   }
            // },
            getAuthToken: async (callback: (token: string) => void) => {
              try {
                const response = await fetchData(API_ENDPOINTS.STRIPO_GET_AUTH_TOKEN)
                callback(response.data.token);
              } catch (error) {
              }
            },
            conditionsEnabled: true,
            // customConditionsEnabled: true,
            conditionCategories: [
              {
                "type": "PREDEFINED",
                "category": "Gender",
                "conditions": [
                  {
                    "id": "1",
                    "name": "Men",
                    "description": "Men only",
                    "beforeScript": "<!-- %IF in_array('men', $CATEGORY) -->",
                    "afterScript": "<!-- %/IF% -->"
                  },
                  {
                    "id": "2",
                    "name": "Women",
                    "description": "Women only",
                    "beforeScript": "<!-- %IF in_array('women', $CATEGORY) -->",
                    "afterScript": "<!-- %/IF% -->"
                  }
                ]
              }
            ],
            // onTemplateLoaded:()=>{
            //   context.setIsLoading(false)
            // }
          });
        };
        document.body.appendChild(script);
      }
      initStripo();
    }

    return () => {
      window?.StripoApi?.stop();
      document.getElementById("stripoScript")?.remove();
    }
  }, [html, css, predefinedFieldsLoading, fieldsRequested, rssTagsReady, rssFeedId]);





  return (
    <div className="stripo">
      {rssFeedId && (
        <Alert variant="info" className="rss-feed-note mb-2">
          <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
          <strong>How to use RSS:</strong>{" "}
          Set the feed URL in{" "}
          <Link to="/org/settings/profile/editProfile">Edit Profile</Link>
          . In the editor, click a <strong>Text</strong> box on the canvas, then use the{" "}
          <strong>merge-tag</strong> icon in the toolbar above it and choose{" "}
          <strong>RSS Feed</strong>.{" "}
          <strong>Feed</strong> = site name,{" "}
          <strong>Item</strong> = one newsletter,{" "}
          <strong>Inner</strong> = extra cards on that newsletter.{" "}
          Use <strong>Default RSS Feed Block</strong> for a default layout. Click Preview to check.
        </Alert>
      )}
      <div className="stripo__header main-title d-flex align-items-center justify-content-start">
        <span className="d-lg-none cursor-pointer" onClick={() => onShowSidebar()}>
          <FontAwesomeIcon icon={faBars} />
        </span>
        <Button id="codeEditor" className="csv-download-btn-style">View Html</Button>
        {/* <Button className="csv-download-btn-style" onClick={()=>navigate("/org/content/pre-built-templates")}>Pre-Built Templates</Button> */}
        <Form.Select value={rssFeedId || ''} onChange={(event) => onRssFeedChange?.(event.target.value)} className="rss-feed-select" aria-label="RSS Feed selection">
          <option value="">Select RSS Feed</option>
          {rssFeeds.map((feed: any) => <option key={feed.id} value={feed.id}>{feed.feed_name || feed.feed_url}</option>)}
        </Form.Select>
        {rssFeedId && (
          <div className="d-flex align-items-center ms-2">
            <span className="me-1 text-nowrap" style={{ fontSize: '13px', fontWeight: 500, color: '#495057' }}>Articles:</span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="rss-articles-tooltip" className="text-start">
                  Controls how many articles/items to display from this RSS feed in the email (between 1 and 10, default is 3).
                </Tooltip>
              }
            >
              <span className="cursor-pointer text-muted me-2" style={{ fontSize: '13px' }}>
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </OverlayTrigger>
            <Form.Control
              type="number"
              min={1}
              max={10}
              style={{ width: '68px', height: '36px', padding: '4px 8px' }}
              value={rssMaxArticles ?? 3}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                onRssMaxArticlesChange?.(Number.isFinite(val) ? Math.min(10, Math.max(1, val)) : 3);
              }}
              onKeyDown={(e: any) => { if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault(); }}
            />
          </div>
        )}
        <button id="undoButton">Undo</button>
        <button id="redoButton">Redo</button>
      </div>
      <div className="stripo__content">
        <div id="stripoSettingsContainer"></div>
        <div id="stripoPreviewContainer">
          <XLoader />
        </div>
      </div>
    </div>
  );
}
